<script setup lang="ts">
/*
* Компонент AuditCalendar - Календарь аудитов
*
* Основные функции:
* - Отображение плановых и внеплановых аудитов в виде календаря
* - Фильтрация по типам аудитов, статусам и прочим параметрам
* - Взаимодействие с FullCalendar (переключение видов, дат)
*/

// Импорт базовых функций Vue для реактивности и хуков жизненного цикла
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
// Импорт компонента FullCalendar
import FullCalendar from '@fullcalendar/vue3'
// Импорт хранилища календаря аудитов
// Импорт хука для определения размера экрана
import { useDisplay, useTheme } from 'vuetify/framework'

// Импорт плагинов для FullCalendar
// Плагин сетки по дням
import dayGridPlugin from '@fullcalendar/daygrid'
// Плагин отображения списком
import listPlugin from '@fullcalendar/list'
// Плагин для взаимодействия (клики, перетаскивание)
import interactionPlugin from '@fullcalendar/interaction'
// Плагин для многомесячного вида
import multiMonthPlugin from '@fullcalendar/multimonth'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
// Инициализация русской локали для календаря
import ruLocale from '@fullcalendar/core/locales/ru'
import { useConfigStore } from '@core/stores/config'
import { useAuditCalendarStore } from '@/modules/audits/stores/useAuditCalendarStore/useAuditCalendarStore'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import CalendarSidebarDatePicker from '@/modules/calendar/components/CalendarSidebarDatePicker.vue'
import CalendarYearGrid from '@/modules/calendar/components/CalendarYearGrid.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoReload } from '@/modules/onboarding/composables/useOnboardingDemoReload'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'

import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import type { IYearGridEvent } from '@/modules/calendar/components/CalendarYearGrid.vue'
import { getAuditTypeColor } from '@/modules/calendar/utils/getCalendarEntityColor'
import { getCalendarEventColors } from '@/modules/calendar/utils/getCalendarEventColors'
import { getCalendarEventLabel } from '@/modules/calendar/utils/getCalendarEventLabel'
import { isDateInRange } from '@/modules/calendar/utils/isDateInRange'
import { useCalendarEventTooltip } from '@/modules/calendar/composables/useCalendarEventTooltip'
import FormAudit from '@/modules/audits/stores/useAuditRegisterStore/components/FormAudit.vue'

// Инициализация хранилища календаря аудитов
const store = useAuditCalendarStore()
const auditRegisterStore = useAuditRegisterStore()
const auditGroupStore = useAuditGroupStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()
// Определение текущего размера экрана (для мобильной адаптации)
const { xs, mdAndDown } = useDisplay()
const theme = useTheme()
const configStore = useConfigStore()
const { isVerticalNavCollapsed } = storeToRefs(configStore)

// Флаг первичной загрузки данных
const isInitialLoading = ref(true)
// Флаг видимости модалки создания проверки
const showCreateDialog = ref(false)

/* Фильтры графика ещё не подключены к API — держим разметку скрытой до реализации */
const isFiltersEnabled = false
// Хук для управления левой боковой панелью
const { isLeftSidebarOpen } = useResponsiveLeftSidebar()

// Онбординг графика: на узком экране боковая панель скрыта — шаги по ней открывают её
useOnboardingContext({
  names: [AUDITS_CONTEXTS.chart],
  root: () => document.body,
  actions: {
    openSidebar: () => { isLeftSidebarOpen.value = true },
  },
})
useOnboardingDemoReload('audit', async () => {
  await store.fetchResources()
  await loadCurrentEvents()
})
// Текущий диапазон дат, отображаемый в календаре
const currentRange = ref({ start: '', end: '' })
const currentView = ref('resourceTimelineYear')

// Мини-календарь сайдбара: выбранная дата синхронизируется с FullCalendar
const refCalendar = ref<InstanceType<typeof FullCalendar> | null>(null)
const sidebarDate = ref<Date>(new Date())

const { hoveredEvent, onEventMouseEnter, onEventMouseLeave } = useCalendarEventTooltip()

const calendarViewOptions = [
  { value: 'resourceTimelineYear', label: 'Матрица' },
  { value: 'multiMonthYear', label: 'Год' },
  { value: 'dayGridMonth', label: 'Месяц' },
  { value: 'dayGridWeek', label: 'Неделя' },
  { value: 'listDay', label: 'День' },
  { value: 'listMonth', label: 'Список' },
]

const changeCalendarView = (view: string) => {
  refCalendar.value?.getApi().changeView(view)
}

const findDepartmentNode = (nodes: ITreeNode[], id: string): ITreeNode | undefined => {
  for (const node of nodes) {
    if (node.key === id) return node

    const child = findDepartmentNode(node.children, id)
    if (child) return child
  }
}

watch(sidebarDate, date => {
  refCalendar.value?.getApi().gotoDate(date)
})

/*
 * Сайдбар меняет ширину контента за ~0.25s (CSS transition).
 * FullCalendar сам не ловит этот reflow — без updateSize плывут колонки матрицы.
 */
let calendarResizeTimer: number | undefined

const refreshCalendarSize = () => {
  window.clearTimeout(calendarResizeTimer)
  calendarResizeTimer = window.setTimeout(() => {
    refCalendar.value?.getApi().updateSize()
  }, 280)
}

watch(isVerticalNavCollapsed, refreshCalendarSize)

onUnmounted(() => {
  window.clearTimeout(calendarResizeTimer)
})

/* Пока событий и ресурсов нет, сетка строится по неполным данным — прячем её */
const isEventsLoading = ref(false)

const loadCurrentEvents = async () => {
  if (!currentRange.value.start) return

  isEventsLoading.value = true

  try {
    await store.fetchAuditCalendarEvents(
      currentRange.value.start,
      currentRange.value.end,
      currentView.value === 'resourceTimelineYear' ? 'departments' : undefined,
    )
  } finally {
    isEventsLoading.value = false
  }
}

const isDarkTheme = computed(() => theme.global.current.value.dark)
const resolveAuditTypeColor = (auditType?: string) =>
  getAuditTypeColor(auditType, isDarkTheme.value)

const resolveEventColor = (event: { extendedProps?: { audit_type?: string, is_overdue?: boolean } }) => {
  if (event.extendedProps?.is_overdue)
    return resolveAuditTypeColor('overdue')

  return resolveAuditTypeColor(event.extendedProps?.audit_type)
}

// Режим «Год» рисуется отдельным компонентом, сетка FullCalendar в нём скрыта
const isYearView = computed(() => currentView.value === 'multiMonthYear')
const currentYear = computed(() => Number(currentRange.value.start.slice(0, 4)))

/*
* В матрице события приходят с `resource=departments` — по одному на каждое
* отделение аудита. В годовой сетке отделений нет, поэтому оставляем по одному
* событию на аудит.
*/
const yearGridEvents = computed(() => {
  const seen = new Set<string>()

  return store.events.reduce<IYearGridEvent[]>((acc, event) => {
    const id = String(event.id)

    if (seen.has(id)) return acc

    seen.add(id)
    acc.push({
      id,
      label: getCalendarEventLabel(event),
      title: event.title || 'Без названия',
      start: event.start,
      url: event.url,
      color: resolveEventColor(event),
    })

    return acc
  }, [])
})

// Инициализация компонента при монтировании
onMounted(async () => {
  await Promise.all([
    store.fetchAuditTypes(),
    store.fetchResources(),
  ])

  // Снятие флага первичной загрузки, разрешаем последующие запросы
  isInitialLoading.value = false

  await loadCurrentEvents()
})

// Логика чекбокса "Выбрать все" типы аудитов
const checkAll = computed({
  // Возвращает true, если выбраны все типы
  get: () => store.selectedAuditTypes.length === store.auditTypes.length && store.auditTypes.length > 0,
  // Устанавливает все типы аудитов при выборе
  set: val => {
    store.selectedAuditTypes = val ? store.auditTypes.map(i => i.id) : []
  },
})

// Следим за состоянием чекбоксов типов и обновляем события календаря
watch(() => store.selectedAuditTypes, () => {
  // Если первичная загрузка завершена и диапазон дат установлен
  if (!isInitialLoading.value && currentRange.value.start) {
    loadCurrentEvents()
  }
}, { deep: true })

// Вычисляемое свойство: объект настроек для FullCalendar
const calendarOptions = computed(() => {
  return {
    // Подключенные плагины календаря
    schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
    plugins: [
      dayGridPlugin,
      interactionPlugin,
      listPlugin,
      multiMonthPlugin,
      resourceTimelinePlugin,
    ],
    // Установка русской локали
    locale: ruLocale,
    // Начальный вид: месяц
    initialView: 'resourceTimelineYear',
    customButtons: {
      drawerToggler: {
        text: '',
        click: () => {
          isLeftSidebarOpen.value = true
        },
      },
    },
    // Настройка шапки календаря
    headerToolbar: {
      start: 'drawerToggler,prev,next title',
      end: 'resourceTimelineYear,multiMonthYear,dayGridMonth,dayGridWeek,listDay,listMonth',
    },
    // Настройки для различных видов календаря
    views: {
      resourceTimelineYear: {
        buttonText: 'Матрица',
        slotDuration: { months: 1 },
        slotLabelFormat: { month: 'long' as const },
      },
      multiMonthYear: { buttonText: 'Год' },
      /*
      * Число, не true: при contentHeight auto true не срабатывает,
      * и чипы на узкой ячейке наезжают друг на друга.
      */
      dayGridMonth: { buttonText: 'Месяц', dayMaxEvents: 3 },
      dayGridWeek: { buttonText: 'Неделя' },
      listDay: { buttonText: 'День' },
      listMonth: { buttonText: 'Список' },
    },
    /*
    * Подписи кнопок видов заданы в `views`. Здесь оставлен только `today`:
    * верхнеуровневый `buttonText.list` перебивает `views.listDay.buttonText`,
    * из-за чего кнопка «День» превращалась в «Список».
    */
    buttonText: {
      today: 'Сегодня',
    },
    // Текст для событий на весь день
    allDayText: 'Весь день',
    moreLinkText: xs.value ? (num: number) => `+${num}` : 'ещё',
    // Текст, когда нет событий для отображения
    noEventsText: 'Нет событий для отображения',

    /*
    * По умолчанию событие со временем рисуется точкой с текстом, а событие
    * на весь день — блоком. В макете все события одинаковые чипы.
    */
    eventDisplay: 'block',
    displayEventTime: false,
    resourceAreaWidth: xs.value ? '30%' : '20%',
    resourceAreaHeaderContent: 'Отделения',
    resources: store.resources,
    height: currentView.value === 'resourceTimelineYear'
      ? (xs.value ? 'calc(100vh + 4rem)' : '90vh')
      : undefined,

    /*
    * В месяце высоту задают строки (125px из макета), а не пропорция от
    * ширины: иначе на широких экранах ячейки растягиваются вдвое.
    */
    contentHeight: currentView.value === 'dayGridMonth' ? 'auto' : undefined,

    // Хук, срабатывающий при смене диапазона дат
    datesSet: (dateInfo: any) => {
      const viewChanged = currentView.value !== dateInfo.view.type

      currentView.value = dateInfo.view.type

      /*
      * Год и матрица стоят на 1 января, и при переключении FullCalendar
      * сохраняет эту дату — иначе месяц/неделя/день открывались бы в январе.
      */
      if (viewChanged && !isDateInRange(new Date(), dateInfo.start, dateInfo.end)) {
        refCalendar.value?.getApi().today()

        return
      }

      /*
      * Навигация тулбаром двигает и мини-календарь сайдбара. Сравнение по
      * времени обязательно: `watch(sidebarDate)` зовёт `gotoDate`, а тот снова
      * вызывает `datesSet` — с новым объектом Date это уходит в рекурсию.
      */
      if (
        dateInfo.view.type !== 'resourceTimelineYear'
        && sidebarDate.value.getTime() !== dateInfo.view.currentStart.getTime()
      ) {
        sidebarDate.value = dateInfo.view.currentStart
      }

      // Форматирование даты начала (YYYY-MM-DD)
      const start = dateInfo.startStr.slice(0, 10)
      // Форматирование даты окончания (YYYY-MM-DD)
      const end = dateInfo.endStr.slice(0, 10)

      // Если диапазон изменился
      if (
        viewChanged
        || currentRange.value.start !== start
        || currentRange.value.end !== end
      ) {
        // Устанавливаем новый диапазон
        currentRange.value = { start, end }

        // Запрашиваем события для нового диапазона, если загрузка завершена
        if (!isInitialLoading.value) {
          loadCurrentEvents()
        }
      }
    },

    dateClick: async (info: {
      dateStr: string
      resource?: {
        id: string
        title: string
      }
    }) => {
      const isMatrixView = currentView.value === 'resourceTimelineYear'
      const isDateGridView = ['dayGridMonth', 'dayGridWeek'].includes(currentView.value)

      if (!isMatrixView && !isDateGridView) return
      if (isMatrixView && !info.resource) return

      let plannedStart = info.dateStr.slice(0, 10)

      if (isMatrixView) {
        const year = Number(info.dateStr.slice(0, 4))
        const month = Number(info.dateStr.slice(5, 7))
        const day = sidebarDate.value.getDate()
        const daysInMonth = new Date(year, month, 0).getDate()

        if (day > daysInMonth) {
          feedbackStore.showToast({
            type: 'error',
            message: `В выбранном месяце нет ${day} числа (дней в месяце: ${daysInMonth}). Выберите в календаре другую дату начала проверки`,
          })
          return
        }

        plannedStart = [
          year,
          String(month).padStart(2, '0'),
          String(day).padStart(2, '0'),
        ].join('-')
      }

      let department

      if (isMatrixView && info.resource) {
        if (!apiStore.departmentsTree.length) {
          await apiStore.fetchAllDepartments()
        }

        /*
        * Ресурсы матрицы приходят из календаря и шире справочника
        * `/api/employee/department/`, на котором построена форма: часть отделений
        * в нём отсутствует, выбрать их в форме нельзя.
        */
        department = findDepartmentNode(apiStore.departmentsTree, info.resource.id)
        if (!department) {
          feedbackStore.showToast({
            type: 'error',
            message: 'Нельзя создавать новые проверки в выбранном отделении',
          })
          return
        }
      }

      auditRegisterStore.resetAudit()
      auditGroupStore.setEmptyGroup()
      auditRegisterStore.currentAudit.planned_start = plannedStart

      if (department) {
        auditRegisterStore.currentAudit.departments = [{
          id: department.key,
          name: department.label,
          short_name: department.label,
        }]
      }

      // Baseline от кода: автозаполнение не считается изменением формы
      auditRegisterStore.editableAudit = JSON.parse(JSON.stringify(auditRegisterStore.currentAudit))

      showCreateDialog.value = true
    },

    // Маппинг событий из хранилища в формат FullCalendar
    events: store.events.map(ev => ({
      id: ev.resourceId ? `${ev.id}-${ev.resourceId}` : ev.id?.toString(),
      resourceId: ev.resourceId ?? undefined,
      title: ev.title || 'Без названия',
      start: ev.start,
      end: ev.end,
      allDay: ev.allDay ?? true,
      url: ev.url,
      ...getCalendarEventColors(resolveEventColor(ev)),
      extendedProps: {
        ...ev.extendedProps,
        label: getCalendarEventLabel(ev),
        compactLabel: getCalendarEventLabel(ev, { compact: true }),
      },
    })),

    /* В сетке событие подписано номером, полное название — в тултипе */
    eventContent: (arg: any) => {
      const node = document.createElement('span')
      const { label, compactLabel } = arg.event.extendedProps

      node.textContent = xs.value ? compactLabel : label

      return { domNodes: [node] }
    },

    eventMouseEnter: onEventMouseEnter,
    eventMouseLeave: onEventMouseLeave,

    // Обработчик системного клика по событию
    eventClick: (info: any) => {
      // Предотвращение стандартного поведения браузера
      if (info.jsEvent) info.jsEvent.preventDefault()
      // Переход по URL события в новой вкладке
      if (info.event.url) window.open(info.event.url, '_blank')
    },
  }
})

// Открытие модалки создания проверки (та же форма, что в реестре аудитов)
const openCreateAudit = () => {
  auditRegisterStore.resetAudit()
  auditGroupStore.setEmptyGroup()
  showCreateDialog.value = true
}

const onCreateClose = async () => {
  showCreateDialog.value = false
  await loadCurrentEvents()
}

</script>

<template>
  <div class="d-flex flex-column gap-3">
    <div v-if="isFiltersEnabled" class="d-flex align-center gap-2 w-100 justify-space-between" :class="{ 'flex-column': xs }">
      <div class="d-flex align-center gap-2" :class="{ 'flex-column w-100': xs }">
        <VBtn
          :class="{ 'w-100': xs }"
          prepend-icon="ri-filter-2-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
        >
          Отделение
        </VBtn>
        <VBtn
          :class="{ 'w-100': xs }"
          prepend-icon="ri-filter-2-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
        >
          Статус
        </VBtn>
        <VBtn
          :class="{ 'w-100': xs }"
          prepend-icon="ri-filter-2-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
        >
          Требования
        </VBtn>
        <VBtn
          :class="{ 'w-100': xs }"
          prepend-icon="ri-filter-2-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
        >
          Мои аудиты
        </VBtn>
      </div>

    </div>

    <VExpansionPanels v-if="xs" class="w-100">
      <VExpansionPanel>
        <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
          <div class="d-flex align-center gap-2">
            <VIcon icon="ri-calendar-2-line" size="20" />
            <span class="text-subtitle-1 font-weight-medium">Вид календаря</span>
          </div>
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div class="d-flex flex-column gap-2 w-100">
            <VBtn
              v-for="view in calendarViewOptions"
              :key="view.value"
              class="w-100"
              :active="currentView === view.value"
              variant="outlined"
              color="secondary"
              @click="changeCalendarView(view.value)"
            >
              {{ view.label }}
            </VBtn>
          </div>
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <VCard>
      <VLayout style="z-index: 0;">
        <VNavigationDrawer
          v-model="isLeftSidebarOpen"
          width="344"
          absolute
          touchless
          location="start"
          class="calendar-add-event-drawer"
          :temporary="mdAndDown"
        >
          <div class="d-flex align-center gap-2 pa-5">
            <VBtn class="flex-grow-1" prepend-icon="ri-add-line" :data-onboarding="AUDITS_ANCHORS.chart.create" @click="openCreateAudit">
              Создать проверку
            </VBtn>
            <VBtn
              v-if="xs"
              icon="ri-close-line"
              variant="text"
              aria-label="Закрыть календарь"
              @click="isLeftSidebarOpen = false"
            />
          </div>

          <VDivider />

          <CalendarSidebarDatePicker v-model="sidebarDate" :data-onboarding="AUDITS_ANCHORS.chart.datePicker" />

          <VDivider />
          <div class="pa-5" :data-onboarding="AUDITS_ANCHORS.chart.types">
            <h5 class="text-h5 mb-4">
              Вид аудита
            </h5>
            <div class="d-flex flex-column calendars-checkbox">
              <VCheckbox
                v-model="checkAll"
                label="Все виды"
                class="mb-2"
              />
              <VCheckbox
                v-for="auditType in store.auditTypes"
                :key="auditType.id"
                v-model="store.selectedAuditTypes"
                :value="auditType.id"
                :label="auditType.title"
                :color="resolveAuditTypeColor(auditType.id)"
              />
            </div>
          </div>
        </VNavigationDrawer>

        <VMain>
          <VCard flat>
            <!-- Класс на обычном div: VCard не пробрасывает его на корневой элемент -->
            <div
              :class="{
                'calendar--year-mode': isYearView,
                'calendar--loading': isEventsLoading,
              }"
              :data-onboarding="AUDITS_ANCHORS.chart.calendar"
            >
              <FullCalendar ref="refCalendar" :options="calendarOptions" />

              <VTooltip
                v-if="hoveredEvent"
                :activator="hoveredEvent.el"
                :text="hoveredEvent.title"
                location="top"
                model-value
              />
            </div>

            <CalendarYearGrid
              v-if="isYearView"
              :events="yearGridEvents"
              :year="currentYear"
            />
          </VCard>
        </VMain>
      </VLayout>
    </VCard>

    <FormAudit v-if="showCreateDialog" embedded @close="onCreateClose" />
  </div>
</template>

<style lang="scss" scoped>
@use "@core/scss/template/libs/full-calendar";
.calendars-checkbox {
  .v-label {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    opacity: var(--v-high-emphasis-opacity);
  }
}
:deep(.v-layout) {
  overflow: visible !important;
  .v-card { overflow: visible; }
}

/* В режиме «Год» от FullCalendar нужен только тулбар: сетку заменяет CalendarYearGrid */
:deep(.calendar--year-mode .fc-view-harness) {
  display: none;
}

/*
* На время загрузки сетка скрыта, но занимает место: FullCalendar считает
* размеры по видимой области, а `display: none` их обнуляет.
*/
:deep(.calendar--loading .fc-view-harness) {
  visibility: hidden;
}

@media (max-width: 599px) {
  /* На мобильном виды вынесены в отдельную сворачиваемую панель */
  :deep(.fc-button-group:has(.fc-dayGridMonth-button)) {
    display: none !important;
  }
}
</style>
